import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/**
 * Dev-overlay noise filter.
 *
 * Two messages emitted by third-party embeds (notably the Cognito Forms
 * `seamless.js` script used on /invest) get flagged as fatal by
 * webpack-dev-server's runtime-error overlay even though they aren't:
 *
 *   1. `ResizeObserver loop completed with undelivered notifications.`
 *      Benign warning fired when a ResizeObserver callback resizes the
 *      observed element on the same frame. Spec authors classify it as
 *      non-error; Chrome itself filters it from its console.
 *
 *   2. `Script error.` (no message, no source)
 *      Browser placeholder for uncaught errors inside cross-origin
 *      scripts. We can't read the real message for security reasons,
 *      and we can't fix code we don't own.
 *
 * Strategy:
 *   • Wrap window.ResizeObserver with a requestAnimationFrame-debounced
 *     callback so the loop warning is never produced in the first place.
 *   • Register CAPTURE-phase `error` + `unhandledrejection` listeners
 *     that swallow both target patterns BEFORE webpack-dev-server's
 *     bubble-phase listener can paint the overlay.
 *
 * Every other error and rejection propagates normally so real bugs
 * still surface in the overlay / console.
 */
const IGNORED_MESSAGES = [
  /ResizeObserver loop completed/i,
  /ResizeObserver loop limit exceeded/i,
  /^Script error\.?$/i,
];

const shouldSwallow = (msg) =>
  typeof msg === "string" && IGNORED_MESSAGES.some((re) => re.test(msg));

// 1. RAF-debounce ResizeObserver callbacks so the loop warning never fires.
if (typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let scheduled = false;
      let lastEntries = [];
      let lastObserver = null;
      const wrapped = (entries, observer) => {
        lastEntries = entries;
        lastObserver = observer;
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          try {
            callback(lastEntries, lastObserver);
          } catch (e) {
            // Surface only non-loop errors; the loop warning is already neutered.
            if (!shouldSwallow(e?.message)) throw e;
          }
        });
      };
      super(wrapped);
    }
  };
}

// 2. Capture-phase listeners — beat webpack-dev-server's overlay to the punch.
const errorHandler = (e) => {
  const msg = (e && (e.message || e.error?.message)) || "";
  if (shouldSwallow(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
};

const rejectionHandler = (e) => {
  const reason = e?.reason;
  const msg = (reason && (reason.message || String(reason))) || "";
  if (shouldSwallow(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
};

window.addEventListener("error", errorHandler, true);
window.addEventListener("unhandledrejection", rejectionHandler, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
